<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { useI18n } from "vue-i18n"
import { computed } from "vue"

// Types
import { LineBlockLine } from "@types"

// Store
import { useSourceStore } from "@core/stores/source.store"

const sourceStore = useSourceStore()

import { formatNumber, formatDate } from "@core/helpers"
import { SOURCES_TYPES } from "@core/constants"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            kva: "kVA",
            v: "V",
            a: "A",
            pdl: {
                localisation: "Localisation",
                calibre: "Calibre du disjoncteur",
                tension: "Tension de livraison",
                domaine_tension: "Domaine de tension",
                puiss_soutirage: "Puiss. de racc. en soutirage",
                calendrier: "Calendrier fournisseur",
                fta: "FTA",
                puiss_souscrite: "Puiss. souscrite",
                branchement_triphase: "Branchement",
                triphase: "Triphasé",
                monophase: "Monophasé",
                denivele: "Dénivelé de puissance",
                pte: "PTE",
                saison_haute: "Saison Haute",
                saison_basse: "Saison Basse",
                niveau_ouverture: "Niveau d’ouverture",
                teleoperable: "Téléopérable",
                teleoperable_yes: "Oui",
                teleoperable_no: "Non",
                hp: "HP",
                hc: "HC",
                tooltip: {
                    fta: "La FTA (Formule tarifaire d'acheminement) permet de déterminer la grille tarifaire utilisée par Enedis pour la facturation liée à l'acheminement de l'électricité (composante de soutirage).",
                    localisation:
                        "Code permettant de situer le PDL dans son espace. Les valeurs affichées sont : Local, Cave, Gaine, Spécial, Escalier ou Accès.",
                    denivele:
                        "Le dénivelé de puissance correspond aux puissances souscrites sur les différentes classes temporelles.",
                    niveau_ouverture:
                        "Niveau d’ouverture aux services. Un Linky de niveau 2 a le niveau maximum de services disponibles. Valeurs : 0, 1, 2.",
                    teleoperable:
                        "Un compteur non téléopérable ne permet pas à Enedis de remonter courbe de charge et données journalières de consommation à distance.",
                },
            },
            pce: {
                date_mise_en_service: "Date de mise en service",
                profil: "Profil type actuel",
                zone_gaz: "Zone gazière",
                cja: "Capacité journalière d'acheminement (CJA)",
                car: "Consommation annuelle de référence (CAR)",
                pression_livraison: "Pression de livraison",
                matricule_compteur: "Matricule du compteur",
                code_calibre: "Code calibre",
                code_debit: "Code débit",
                code_debit_normalise: "Code débit normalisé",
                date_profil_actuel: "Date du profil type actuel",
                tooltip: {
                    date_mise_en_service:
                        "Date de mise en service (MES) du PCE pour le titulaire actif, telle que remontée par GRDF. GRDF ne transmet plus de données antérieures à cette date.",
                },
            },
            dalkia: {
                numero_serie: "Numéro de série",
            },
            eaubordeaux: {
                numero_serie: "Numéro de série",
            },
            cpcu: {
                puiss_souscrite: "Puissance souscrite",
                puissance_facture: "Puissance facturable",
                freq_facture: "Fréquence de facturation",
                index_k1: "Coefficient d'indexation K1",
                index_k2: "Coefficient d'indexation K2",
            },
            elecsavoie: {
                denivele: "Dénivelé de puissance",
                service: "Service",
                client_type: "Type de client",
                puissance_souscrite_max: "Puiss. souscrite",
                tooltip: {
                    denivele:
                        "Le dénivelé de puissance correspond aux puissances souscrites sur les différentes classes temporelles.",
                },
            },
            regaz: {
                identifiant_compteur: "Identifiant du compteur",
                frequence_facturable: "Fréquence de facturation",
                profil: "Profil type actuel",
                car: "Consommation annuelle de référence (CAR)",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    meterId: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Computed pour récupérer les infos de la source
 */
const getSourceById = computed(() => {
    return sourceStore.getSourceById(props.meterId)
})

/**
 * Computed pour récupérer les infos techniques : TODO simplifié / refactoriser
 */
const getTechnicalDataByType = computed(() => {
    let result: LineBlockLine[] = []
    switch (getSourceById.value?.type) {
        case SOURCES_TYPES.PCE:
            result = getTechnicalPCE()
            break
        case SOURCES_TYPES.PDL:
            result = getTechnicalPDL()
            break
        case SOURCES_TYPES.CPCU:
            result = getTechnicalCPCU()
            break
        case SOURCES_TYPES.DALKIA:
            result = getTechnicalDalkia()
            break
        case SOURCES_TYPES.REGAZ:
            result = getTechnicalRegaz()
            break
        case SOURCES_TYPES.ELECSAVOIE:
            result = getTechnicalElecsavoie()
            break
        case SOURCES_TYPES.EAU_BORDEAUX:
            result = getTechnicalEauBordeaux()
            break
    }
    return result
})

function formatValue(
    value: string | number | boolean | null | undefined,
    unit?: string
): string {
    if (value == null) return "-"
    else if (unit) return `${value} ${unit}`
    else return value
}

/**
 * Retourne les données techniques pour le cas PDL
 */
const getTechnicalPDL = () => {
    const pdl = getSourceById?.value?.informations?.pdl

    if (!pdl) return []
    const pdlData: LineBlockLine[] = [
        {
            text: t("pdl.localisation"),
            value: formatValue(pdl?.compteur?.localisation),
            tooltip: t("pdl.tooltip.localisation"),
        },
        {
            text: t("pdl.niveau_ouverture"),
            value: formatValue(pdl?.compteur?.niveau_ouverture_services),
            tooltip: t("pdl.tooltip.niveau_ouverture"),
        },
        {
            text: t("pdl.teleoperable"),
            value:
                pdl?.compteur?.teleoperable === true
                    ? t("pdl.teleoperable_yes")
                    : pdl?.compteur?.teleoperable === false
                      ? t("pdl.teleoperable_no")
                      : "-",
            tooltip: t("pdl.tooltip.teleoperable"),
        },
        {
            text: t("pdl.calibre"),
            value: formatValue(pdl?.compteur?.calibre_disjoncteur, t("a")),
        },
        {
            text: t("pdl.tension"),
            value: formatValue(pdl?.compteur?.tension_livraison),
        },
        {
            text: t("pdl.domaine_tension"),
            value: formatValue(pdl?.compteur?.domaine_tension),
        },
        {
            text: t("pdl.puiss_soutirage"),
            value: formatValue(
                pdl?.compteur?.raccordement?.puissance_raccordement_soutirage,
                t("kva")
            ),
        },
        {
            text: t("pdl.calendrier"),
            value: formatValue(
                pdl?.structure_tarifaire?.calendrier_fournisseur?.libelle
            ),
        },
        {
            text: t("pdl.fta"),
            value: formatValue(
                pdl?.structure_tarifaire?.formule_tarifaire_acheminement?.fta
            ),
            tooltip: t("pdl.tooltip.fta"),
        },
    ]
    if (["C5", "P4"].includes(pdl?.compteur?.segment || "")) {
        pdlData.push({
            text: t("pdl.branchement_triphase"),
            value: formatValue(
                pdl?.compteur?.raccordement?.branchement_triphase
                    ? t("pdl.triphase")
                    : pdl?.etat === "inexistant"
                      ? "-"
                      : t("pdl.monophase")
            ),
        })
    }
    if (pdl?.structure_tarifaire?.puissance_souscrite_soutirage?.puissance) {
        pdlData.push({
            text: t("pdl.puiss_souscrite"),
            value: formatValue(
                pdl?.structure_tarifaire?.puissance_souscrite_soutirage
                    ?.puissance,
                t("kva")
            ),
        })
    }
    if (pdl?.structure_tarifaire?.puissance_souscrite_soutirage?.denivele)
        pdlData.push({
            id: "denivele",
            text: t("pdl.denivele"),
            value: pdl?.structure_tarifaire?.puissance_souscrite_soutirage
                ?.denivele,
            tooltip: t("pdl.tooltip.denivele"),
        })
    return pdlData
}

/**
 * Retourne les données techniques pour le cas PCE
 */
const getTechnicalPCE = () => {
    const pce = getSourceById?.value?.informations?.pce
    return [
        {
            text: t("pce.date_mise_en_service"),
            value: pce?.date_mise_en_service
                ? formatDate(pce.date_mise_en_service)
                : "-",
            tooltip: t("pce.tooltip.date_mise_en_service"),
        },
        {
            text: t("pce.profil"),
            value: formatValue(pce?.compteur.profil),
        },
        {
            text: t("pce.zone_gaz"),
            value: formatValue(pce?.compteur.zone_gaz),
        },
        {
            text: t("pce.cja"),
            value: formatNumber(pce?.compteur.cja_actuelle, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                unit: "kWh",
            }),
        },
        {
            text: t("pce.car"),
            value: formatNumber(pce?.compteur.car_actuelle, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                unit: "kWh",
            }),
        },
        {
            text: t("pce.pression_livraison"),
            value: formatNumber(pce?.compteur.pression_livraison, {
                maximumFractionDigits: 3,
                minimumFractionDigits: 0,
            }),
        },
        {
            text: t("pce.matricule_compteur"),
            value: pce?.compteur.matricule_compteur || "-",
        },
        {
            text: t("pce.code_calibre"),
            value: pce?.compteur.code_calibre || "-",
        },
        {
            text: t("pce.code_debit"),
            value: `${pce?.compteur.code_debit || "-"}`,
        },
        {
            text: t("pce.code_debit_normalise"),
            value: `${pce?.compteur.code_debit_normalise || "-"}`,
        },
        {
            text: t("pce.date_profil_actuel"),
            value: pce?.compteur?.date_profil_actuel
                ? formatDate(pce?.compteur.date_profil_actuel)
                : "-",
        },
    ]
}

/**
 * Retourne les données techniques pour le cas CPCU
 */
const getTechnicalCPCU = () => {
    const cpcu = getSourceById?.value?.informations?.cpcu
    return [
        {
            text: t("cpcu.puiss_souscrite"),
            value: formatNumber(cpcu?.compteur.puissance_souscrite, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                unit: "kVA",
            }),
        },
        {
            text: t("cpcu.puissance_facture"),
            value: formatNumber(cpcu?.compteur.puissance_facturable, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                unit: "kVA",
            }),
        },
        {
            text: t("cpcu.freq_facture"),
            value: formatNumber(cpcu?.compteur.frequence_facturation, {
                maximumFractionDigits: 0,
                minimumFractionDigits: 0,
                unit: "mois",
            }),
        },
        {
            text: t("cpcu.index_k1"),
            value: formatValue(cpcu?.compteur.coefficient_indexation_k1),
        },
        {
            text: t("cpcu.index_k2"),
            value: formatValue(cpcu?.compteur.coefficient_indexation_k2),
        },
    ]
}

/**
 * Retourne les données techniques pour le cas DALKIA
 */
const getTechnicalDalkia = () => {
    const dalkia = getSourceById?.value?.informations?.dalkia
    return [
        {
            text: t("dalkia.numero_serie"),
            value: formatValue(dalkia?.compteur?.numero_serie),
        },
    ]
}

/**
 * Retourne les données techniques pour le cas EAU_BORDEAUX
 */
const getTechnicalEauBordeaux = () => {
    const eaubordeaux = getSourceById?.value?.informations?.eaubordeaux
    return [
        {
            text: t("eaubordeaux.numero_serie"),
            value: formatValue(eaubordeaux?.compteur?.numero_serie),
        },
    ]
}

/**
 * Retourne les données techniques pour le cas REGAZ
 */
function getTechnicalRegaz(): LineBlockLine[] {
    const regaz = getSourceById?.value?.informations?.regaz
    return [
        {
            text: t("regaz.identifiant_compteur"),
            value: formatValue(regaz?.compteur.identifiant_compteur),
        },
        {
            text: t("regaz.frequence_facturable"),
            value: formatValue(regaz?.compteur.frequence_facturation),
        },
        {
            text: t("regaz.profil"),
            value: formatValue(regaz?.compteur.profil),
        },
        {
            text: t("regaz.car"),
            value: formatNumber(regaz?.compteur.car, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
                unit: "KW",
            }),
        },
    ]
}

/**
 * Retourne les données techniques pour le cas ELECSAVOIE
 */
function getTechnicalElecsavoie(): LineBlockLine[] {
    const elecsavoie = getSourceById?.value?.informations?.elecsavoie
    const lines: LineBlockLine[] = [
        {
            text: t("elecsavoie.service"),
            value: formatValue(
                elecsavoie?.structure_tarifaire?.calendrier_fournisseur?.libelle
            ),
        },
        {
            text: t("elecsavoie.client_type"),
            value: formatValue(elecsavoie?.compteur.client_type),
        },
    ]

    if (elecsavoie?.compteur.segment === "C5") {
        lines.push({
            text: t("elecsavoie.puissance_souscrite_max"),
            value: formatNumber(
                elecsavoie?.structure_tarifaire?.puissance_souscrite_soutirage
                    ?.puissance,
                {
                    maximumFractionDigits: 2,
                    minimumFractionDigits: 0,
                    unit: "kW",
                }
            ),
        })
    }
    if (
        elecsavoie?.structure_tarifaire?.puissance_souscrite_soutirage?.denivele
    )
        lines.push({
            id: "denivele",
            text: t("elecsavoie.denivele"),
            value: elecsavoie?.structure_tarifaire
                ?.puissance_souscrite_soutirage?.denivele,
            tooltip: t("elecsavoie.tooltip.denivele"),
        })

    return lines
}
</script>

<template>
    <div class="meter_card_technical_data mt-6" v-if="getTechnicalDataByType">
        <div v-for="(line, index) in getTechnicalDataByType" :key="index">
            <BaseLineBlock :line="line">
                <template #denivele="{ value }">
                    <MeterPdlDeniveleViewer :denivele="value" />
                </template>
            </BaseLineBlock>
        </div>
    </div>
</template>
